import type { AbcSearchItem, MaterialItem } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { errorMessage, get, post } from "../api";
import { Alert, Button, Dialog, Empty, Loading } from "./ui";

/** An ABC item picture, through the server (ABC only serves them to a signed-in caller). */
export const abcImage = (href: string) => `/api/abc/image?href=${encodeURIComponent(href)}`;

/**
 * Searches ABC Supply's catalog (words or an item number) and adds a pick to the catalog, priced at
 * NMT's branch when ABC can. `onPick` gets the catalog item (already there, or just added).
 */
export function AbcSearchDialog({ onClose, onPick, pickLabel = "Add" }: { onClose: () => void; onPick: (item: MaterialItem) => void; pickLabel?: string }) {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setQ(text.trim()), 400);
    return () => clearTimeout(t);
  }, [text]);
  const results = useQuery({ queryKey: ["abc-search", q], queryFn: () => get<AbcSearchItem[]>(`/api/abc/search?q=${encodeURIComponent(q)}`), enabled: q.length >= 2, staleTime: 60_000 });
  const add = useMutation({
    mutationFn: (r: AbcSearchItem) => post<MaterialItem>("/api/material-items", { abcItemNumber: r.itemNumber }),
    onSuccess: (item) => {
      void qc.invalidateQueries({ queryKey: ["material-items"] });
      void qc.invalidateQueries({ queryKey: ["material-catalog"] });
      onPick(item);
    },
  });
  return (
    <Dialog title="Search ABC Supply" onClose={onClose} wide>
      <div className="flex flex-col gap-3">
        <input
          autoFocus
          className="w-full rounded-[var(--radius-control)] border border-line bg-white px-3 py-2 text-base sm:text-sm"
          placeholder="Words from the description (starter strip, ridge vent) or an item number"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        {add.error && <Alert>{errorMessage(add.error)}</Alert>}
        {q.length < 2 ? (
          <p className="text-sm text-muted">Type at least two letters.</p>
        ) : results.isLoading ? (
          <Loading />
        ) : results.error ? (
          <Alert>{errorMessage(results.error)}</Alert>
        ) : !results.data?.length ? (
          <Empty>Nothing at ABC matches that.</Empty>
        ) : (
          <ul className="max-h-[28rem] overflow-auto rounded-[var(--radius-control)] border border-mist">
            {results.data.map((r) => (
              <li key={r.itemNumber} className="flex items-center gap-3 border-b border-mist px-3 py-2 last:border-0">
                {r.imageUrl ? <img src={abcImage(r.imageUrl)} alt="" className="h-10 w-10 shrink-0 rounded object-contain" loading="lazy" /> : <div className="h-10 w-10 shrink-0 rounded bg-mist" />}
                <div className="min-w-0 flex-1 text-sm">
                  <div className="font-medium">{r.description}</div>
                  <div className="text-xs text-muted">
                    #{r.itemNumber} · {r.units.map((u) => u.code).join(" / ")}
                    {r.familyName && ` · ${r.familyName}`}
                  </div>
                </div>
                <Button className="shrink-0 px-3 py-1" busy={add.isPending && add.variables?.itemNumber === r.itemNumber} onClick={() => add.mutate(r)}>
                  {r.catalogId ? (pickLabel === "Add" ? "In catalog" : pickLabel) : pickLabel}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Dialog>
  );
}
